import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search, Edit2, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import Wrapper from "../../../components/Wrapper";
import { labApi } from "../../../services/labApi";

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [reagents, setReagents] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showSoilModal, setShowSoilModal] = useState(false);
  
  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({ customer_name: "", due_date: "", notes: "", number_of_tests: "" });
  
  const [customForm, setCustomForm] = useState({ order_number: "", customer_name: "", due_date: "", notes: "" });
  const [soilForm, setSoilForm] = useState({ order_number: "", customer_name: "", test_type: "all_parameters", order_mode: "kit", number_of_kits: "", number_of_tests: "", due_date: "", notes: "" });
  const [orderItems, setOrderItems] = useState([{ reagent_id: "", quantity_required: "" }]);
  
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const toggleExpanded = (id) => setExpandedOrders((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const [fulfillDrafts, setFulfillDrafts] = useState({});
  const [savingItemId, setSavingItemId] = useState(null);
  const [fulfillError, setFulfillError] = useState("");

  const itemRowId = (item) => item?.id ?? item?.item_id;

  const load = useCallback(async () => {
    const [orderRows, reagentRows] = await Promise.all([
      labApi.getOrders({
        status: statusFilter === "all" ? undefined : statusFilter,
        search,
      }),
      labApi.getReagents(),
    ]);

    setOrders(orderRows);
    setReagents(reagentRows);

    const drafts = {};
    orderRows.forEach((o) => {
      (o.items || []).forEach((it) => {
        const rid = itemRowId(it);
        if (rid != null) drafts[rid] = String(it.fulfilled_quantity ?? 0);
      });
    });
    setFulfillDrafts(drafts);
  }, [statusFilter, search]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const generateOrderNumber = () => {
    const date = new Date().toISOString().slice(2, 10).replace(/-/g, "");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `SD${date}${random}`;
  };

  const openCustom = () => {
    setCustomForm({ order_number: generateOrderNumber(), customer_name: "", due_date: "", notes: "" });
    setOrderItems([{ reagent_id: "", quantity_required: "" }]);
    setShowCustomModal(true);
  };

  const openSoil = () => {
    setSoilForm({ order_number: generateOrderNumber(), customer_name: "", test_type: "all_parameters", order_mode: "kit", number_of_kits: "", number_of_tests: "", due_date: "", notes: "" });
    setShowSoilModal(true);
  };

  const openEditModal = (order) => {
    setEditingOrder(order);
    setEditForm({
      customer_name: order.customer_name || "",
      due_date: order.due_date ? String(order.due_date).split('T')[0] : "",
      notes: order.notes || "",
      number_of_tests: order.number_of_tests || "",
    });
  };

  const createCustom = async (e) => {
    e.preventDefault();
    await labApi.createCustomOrder({
      ...customForm,
      items: orderItems
        .filter((i) => i.reagent_id && Number(i.quantity_required) > 0)
        .map((i) => ({ reagent_id: i.reagent_id, quantity_required: Number(i.quantity_required) })),
    });
    setShowCustomModal(false);
    await load();
  };

  const createSoil = async (e) => {
    e.preventDefault();
    if (!soilForm.due_date) {
      setFulfillError("Due date is required for soil test orders.");
      return;
    }
    const computedTests = soilForm.order_mode === "kit"
      ? Number(soilForm.number_of_kits || 0) * 25
      : Number(soilForm.number_of_tests || 0);
    try {
      await labApi.createSoilOrder({
        ...soilForm,
        number_of_tests: computedTests,
      });
      setFulfillError("");
      setShowSoilModal(false);
      await load();
    } catch (error) {
      setFulfillError(error?.response?.data?.message || "Unable to create soil test order.");
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      setFulfillError("");
      await labApi.updateOrderDetails(editingOrder.id, {
        customer_name: editForm.customer_name,
        due_date: editForm.due_date || null,
        notes: editForm.notes || null,
        number_of_tests: editForm.number_of_tests ? Number(editForm.number_of_tests) : undefined,
      });
      setEditingOrder(null);
      await load();
    } catch (error) {
      setFulfillError(error?.response?.data?.message || "Failed to update order details.");
    }
  };

  const setStatus = async (id, status) => {
    await labApi.updateOrderStatus(id, status);
    await load();
  };

  const saveFulfilled = async (item) => {
    const rowId = itemRowId(item);
    if (rowId == null) return;
    const raw = fulfillDrafts[rowId];
    const qty = Number(raw);
    
    if (Number.isNaN(qty) || qty < 0) {
      setFulfillError("Enter a valid fulfilled quantity (0 or more).");
      return;
    }
    
    setFulfillError("");
    try {
      setSavingItemId(rowId);
      await labApi.updateOrderItemFulfillment(rowId, qty);
      await load();
    } catch (e) {
      setFulfillError(e?.response?.data?.message || "Could not update fulfilled quantity.");
    } finally {
      setSavingItemId(null);
    }
  };

  const handleDeleteOrder = async (id, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete order ${orderNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setFulfillError("");
      await labApi.deleteOrder(id);
      await load();
    } catch (error) {
      setFulfillError(error?.response?.data?.message || "Failed to delete order.");
    }
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="w-full text-left">
            <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Orders</h2>
            <p className="mt-1 text-slate-500 text-left">Live order tracking</p>
          </div>
          <div className="flex gap-2 self-start">
            <button onClick={openSoil} className="bg-teal-600 hover:bg-teal-700 transition-colors text-white px-4 py-2 rounded-lg">Soil Test Order</button>
            <button onClick={openCustom} className="bg-emerald-600 hover:bg-emerald-700 transition-colors text-white px-4 py-2 rounded-lg inline-flex items-center gap-2">
              <Plus size={16} /> Custom Order
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-3">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." className="w-full pl-10 pr-4 py-2 border rounded-lg" />
            </div>
            <button onClick={() => load().catch(console.error)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">Search</button>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded-lg">
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="shipped">Shipped</option>
            </select>
          </div>
          {fulfillError ? <p className="text-sm text-red-600 font-medium">{fulfillError}</p> : null}
        </div>

        <div className="space-y-3">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl border p-4 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <button
                      onClick={() => toggleExpanded(order.id)}
                      className="p-0.5 rounded text-slate-400 hover:text-slate-700 transition-colors"
                      title={expandedOrders.has(order.id) ? "Collapse items" : "Expand items"}
                    >
                      {expandedOrders.has(order.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {order.order_number}
                    <button onClick={() => openEditModal(order)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-emerald-600 transition-colors">
                      <Edit2 size={14} />
                    </button>
                    <button 
                      onClick={() => handleDeleteOrder(order.id, order.order_number)} 
                      className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-red-600 transition-colors"
                      title="Delete Order"
                    >
                      <Trash2 size={14} />
                    </button>
                  </h3>
                  <p className="text-sm text-slate-500">
                    {order.customer_name} 
                    {order.due_date ? ` • Due: ${String(order.due_date).split('T')[0]}` : ""}
                  </p>
                  {order.created_at && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      Created: {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                  {order.notes && <p className="text-xs text-slate-400 italic mt-0.5">Notes: {order.notes}</p>}
                </div>
                
                <div className="flex items-center gap-2">
                  <select 
                    value={order.status} 
                    onChange={(e) => setStatus(order.id, e.target.value)} 
                    disabled={String(order.status || "").toLowerCase() === "completed"}
                    className="px-2 py-1.5 border rounded bg-slate-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="pending">pending</option>
                    <option value="in_progress">in progress</option>
                    <option value="completed">completed</option>
                    <option value="shipped">shipped</option>
                    <option value="cancelled">cancelled</option>
                  </select>
                </div>
              </div>
              
              {Number(order.total_required) > 0 ? (
                <p className="mt-2 text-xs text-slate-500 bg-slate-100 inline-block px-2 py-1 rounded">
                  Order progress: {Number(order.total_fulfilled || 0)} / {Number(order.total_required)} units fulfilled
                </p>
              ) : null}
              
              {expandedOrders.has(order.id) && (
              <div className="mt-3 space-y-2">
                {(order.items || []).length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No line items</p>
                ) : null}
                {(order.items || []).map((item) => {
                  const rowId = itemRowId(item);
                  const draft = rowId != null ? fulfillDrafts[rowId] : undefined;
                  const displayDraft = draft !== undefined ? draft : String(item.fulfilled_quantity ?? 0);
                  const unitText = item.unit ? ` ${item.unit}` : "";

                  const isInstant = item.preparation_mode === "instant";
                  const hasEnoughStock =
                    isInstant ||
                    Number(item.current_stock || 0) >= Number(item.quantity || item.quantity_required || 0);
                  const stockColorClasses = isInstant
                    ? "border-violet-200 bg-violet-50/30"
                    : hasEnoughStock
                      ? "border-emerald-200 bg-emerald-50/30"
                      : "border-red-200 bg-red-50/30";

                  return (
                    <div
                      key={rowId ?? `${item.reagent_id}-${item.reagent_name}`}
                      className={`flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between transition-colors ${stockColorClasses}`}
                    >
                      <div className="text-sm text-slate-700">
                        <span className="font-medium">{item.reagent_name || item.reagent_id}</span>
                        {item.preparation_mode === "instant" && (
                          <span className="ml-1 text-xs font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                            Instant
                          </span>
                        )}
                        <span className="text-slate-500"> — {item.quantity || item.quantity_required}{unitText} required</span>
                        {item.preparation_mode !== "instant" && (
                          <span className={`ml-2 text-xs font-medium px-1.5 py-0.5 rounded ${hasEnoughStock ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            Stock: {item.current_stock || 0}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 text-sm text-slate-600">
                          <span>Fulfilled</span>
                          <input
                            type="number"
                            min={0}
                            step={1}
                            disabled={rowId == null || savingItemId === rowId || String(order.status || "").toLowerCase() === "completed"}
                            value={displayDraft}
                            onChange={(e) =>
                              rowId != null &&
                              setFulfillDrafts((prev) => ({ ...prev, [rowId]: e.target.value }))
                            }
                            className="w-24 rounded border border-slate-200 px-2 py-1 text-slate-800 bg-white disabled:opacity-50"
                          />
                        </label>
                        <button
                          type="button"
                          disabled={rowId == null || savingItemId === rowId || String(order.status || "").toLowerCase() === "completed"}
                          onClick={() => saveFulfilled(item)}
                          className="rounded-lg bg-slate-800 hover:bg-slate-900 transition-colors px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
                        >
                          {savingItemId === rowId ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          ))}
        </div>

        {/* --- EDIT MODAL --- */}
        {editingOrder && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Edit Order: {editingOrder.order_number}</h3>
              <form onSubmit={submitEdit} className="space-y-3">
                <input value={editForm.customer_name} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} placeholder="Customer Name" className="w-full px-3 py-2 border rounded-lg" required />
                <input value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} type="date" className="w-full px-3 py-2 border rounded-lg text-slate-700" />
                
                {editForm.number_of_tests !== "" && editForm.number_of_tests !== undefined && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Number of Tests</label>
                    <input 
                      value={editForm.number_of_tests} 
                      onChange={(e) => setEditForm({ ...editForm, number_of_tests: e.target.value })} 
                      type="number" min="1" 
                      className="w-full px-3 py-2 border rounded-lg" 
                    />
                    <p className="text-xs text-amber-600 mt-1">Note: Changing this will automatically recalculate required items.</p>
                  </div>
                )}
                
                <textarea 
                  value={editForm.notes} 
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} 
                  placeholder="Order Notes" 
                  className="w-full px-3 py-2 border rounded-lg h-24 resize-none" 
                />
                
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setEditingOrder(null)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 transition-colors text-white rounded-lg">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showCustomModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-3">Create Custom Order</h3>
              <form onSubmit={createCustom} className="space-y-3">
                <input value={customForm.order_number} onChange={(e) => setCustomForm({ ...customForm, order_number: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required />
                <input value={customForm.customer_name} onChange={(e) => setCustomForm({ ...customForm, customer_name: e.target.value })} placeholder="Customer" className="w-full px-3 py-2 border rounded-lg" required />
                <input value={customForm.due_date} onChange={(e) => setCustomForm({ ...customForm, due_date: e.target.value })} type="date" className="w-full px-3 py-2 border rounded-lg" />
                {orderItems.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2">
                    <select value={item.reagent_id} onChange={(e) => setOrderItems((prev) => prev.map((row, i) => i === idx ? { ...row, reagent_id: e.target.value } : row))} className="px-3 py-2 border rounded-lg">
                      <option value="">Reagent</option>
                      {reagents.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <input value={item.quantity_required} onChange={(e) => setOrderItems((prev) => prev.map((row, i) => i === idx ? { ...row, quantity_required: e.target.value } : row))} type="number" placeholder="Quantity" className="px-3 py-2 border rounded-lg" />
                  </div>
                ))}
                <button type="button" onClick={() => setOrderItems((prev) => [...prev, { reagent_id: "", quantity_required: "" }])} className="px-3 py-2 border rounded-lg">+ Add Item</button>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowCustomModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showSoilModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Create Soil Test Order</h3>
              <form onSubmit={createSoil} className="space-y-3">
                <input value={soilForm.order_number} onChange={(e) => setSoilForm({ ...soilForm, order_number: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required />
                <input value={soilForm.customer_name} onChange={(e) => setSoilForm({ ...soilForm, customer_name: e.target.value })} placeholder="Customer" className="w-full px-3 py-2 border rounded-lg" />
                <select value={soilForm.test_type} onChange={(e) => setSoilForm({ ...soilForm, test_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                  <option value="all_parameters">all_parameters</option>
                  <option value="7_parameters">7_parameters</option>
                </select>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Order Type</label>
                  <select
                    value={soilForm.order_mode}
                    onChange={(e) => setSoilForm({ ...soilForm, order_mode: e.target.value, number_of_kits: "", number_of_tests: "" })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="kit">Soil Testing Kit</option>
                    <option value="service">Soil Testing Service</option>
                  </select>
                </div>

                {soilForm.order_mode === "kit" ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">No. of Kits</label>
                      <input
                        value={soilForm.number_of_kits}
                        onChange={(e) => setSoilForm({ ...soilForm, number_of_kits: e.target.value })}
                        type="number"
                        min="1"
                        placeholder="Number of kits"
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                      <p className="text-xs text-slate-400 mt-1">1 kit = 25 tests</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">No. of Tests</label>
                      <input
                        value={soilForm.number_of_kits ? Number(soilForm.number_of_kits) * 25 : ""}
                        readOnly
                        disabled
                        type="number"
                        placeholder="Auto-calculated"
                        className="w-full px-3 py-2 border rounded-lg bg-slate-100 text-slate-500 cursor-not-allowed"
                      />
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">No. of Tests</label>
                    <input
                      value={soilForm.number_of_tests}
                      onChange={(e) => setSoilForm({ ...soilForm, number_of_tests: e.target.value })}
                      type="number"
                      min="1"
                      placeholder="Number of tests"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                )}

                <input value={soilForm.due_date} onChange={(e) => setSoilForm({ ...soilForm, due_date: e.target.value })} type="date" className="w-full px-3 py-2 border rounded-lg" required />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowSoilModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
        
      </div>
    </Wrapper>
  );
}